// client/src/pages/ShopReturns.tsx

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentShop, hasPermission, isOwnerInShopMode } from "@/lib/auth";
import { usePlan } from "@/hooks/usePlan";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  RefreshCw,
  Search,
  ArrowLeft,
  FileText,
  Shield,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getShopReturns, getReturnById } from "@/lib/api";
import { ReturnModal } from "@/components/returns/ReturnModal";
import { formatCurrency } from "@/lib/currency";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useLocation } from "wouter";

export default function ShopReturns() {
  const [, setLocation] = useLocation();
  const currentShop = getCurrentShop();

  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  // ============ OBTENER INFORMACIÓN DEL PLAN ============
  const { can } = usePlan();

  // Log para depuración
  console.log("👑 ShopReturns - ¿Es owner en modo tienda?", isOwnerMode);

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // ========== FUNCIÓN PARA ACTUALIZAR ==========
  const handleRefresh = async () => {
    await refetch();
    toast({
      title: "Actualizado",
      description: "Lista de devoluciones actualizada",
    });
  };

  // ============ VERIFICAR ACCESO: OWNER TIENE ACCESO PERO SUJETO AL PLAN ============
  const hasPermissionOrOwner =
    isOwnerMode || hasPermission("supervisor") || hasPermission("store_manager");
  const hasAccess = hasPermissionOrOwner && can.useReturns();

  console.log("👑 ShopReturns - Acceso:", {
    isOwnerMode,
    hasPermissionAdmin: hasPermission("supervisor"),
    hasPermissionManager: hasPermission("store_manager"),
    hasPermissionOrOwner,
    canUseReturns: can.useReturns(),
    hasAccess,
  });

  // Query para obtener devoluciones
  const {
    data: returnsData,
    isLoading,
    refetch,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      "shop-returns",
      currentShop?.id,
      currentPage,
      itemsPerPage,
      searchTerm,
      startDate,
      endDate,
    ],
    queryFn: () =>
      getShopReturns(currentShop!.id, {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled:
      !!currentShop &&
      (isOwnerMode || hasPermission("supervisor") || hasPermission("store_manager")), // ← MODIFICADO
  });

  if (!hasAccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            {!can.useReturns() ? (
              <>
                <div className="flex justify-center mb-4">
                  <RefreshCw className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Funcionalidad no disponible
                </h3>
                <p className="text-muted-foreground mb-4">
                  El módulo de devoluciones requiere un plan Profesional o
                  Enterprise.
                </p>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = "/owner/plans")}
                  className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  Mejorar Plan
                </Button>
              </>
            ) : (
              <>
                <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Acceso restringido
                </h3>
                <p className="text-muted-foreground">
                  No tienes permisos para ver esta página.
                </p>
                {can.useReturns() && !isOwnerMode && (
                  <p className="text-sm mt-4 text-muted-foreground">
                    Necesitas permisos de administrador o manager para gestionar
                    devoluciones.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleViewDetails = async (returnId: string) => {
    try {
      const result = await getReturnById(returnId);
      if (result.success && result.data) {
        setSelectedReturn(result.data);
        setShowDetailsModal(true);
      } else {
        toast({
          title: "Error",
          description: result.error || "No se pudo cargar la devolución",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800">Completada</Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>
        );
      case "cancelled":
        return <Badge className="bg-red-100 text-red-800">Cancelada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRefundMethodText = (method: string) => {
    switch (method) {
      case "cash":
        return "Efectivo";
      case "card":
        return "Tarjeta";
      case "transfer":
        return "Transferencia";
      case "original":
        return "Mismo método";
      default:
        return method;
    }
  };

  const returns = returnsData?.data || [];
  const total = returnsData?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/shop/dashboard")}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-heading font-bold">Devoluciones</h1>
            {isOwnerMode && (
              <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                👑 Modo Owner
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Gestiona las devoluciones de productos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
            />
            Actualizar
          </Button>
          <Button size="sm" onClick={() => setShowReturnModal(true)}>
            Nueva Devolución
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Búsqueda</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Número de devolución..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fecha desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2 flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
                className="w-full"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de devoluciones */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de Devoluciones</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Error al cargar las devoluciones</p>
            </div>
          ) : returns.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg">No hay devoluciones registradas</p>
              <p className="text-sm mt-2">
                Las devoluciones que proceses aparecerán aquí
              </p>
              {isOwnerMode && (
                <p className="text-xs mt-4 text-purple-600 bg-purple-50 p-2 rounded inline-block">
                  👑 Como owner tienes acceso total para gestionar devoluciones
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Venta Original</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {returns.map((ret) => (
                      <TableRow key={ret.id}>
                        <TableCell className="font-mono text-sm">
                          {ret.returnNumber}
                        </TableCell>
                        <TableCell>
                          {format(
                            new Date(ret.returnDate),
                            "dd/MM/yyyy HH:mm",
                            { locale: es },
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {ret.originalSaleId.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(parseFloat(ret.totalAmount), "CUP")}
                        </TableCell>
                        <TableCell>
                          {getRefundMethodText(ret.refundMethod)}
                        </TableCell>
                        <TableCell>{getStatusBadge(ret.status)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline">
                            {ret.items.length} item
                            {ret.items.length !== 1 ? "s" : ""}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(ret.id)}
                          >
                            Ver detalles
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages} • {total} registros
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de nueva devolución */}
      <ReturnModal
        open={showReturnModal}
        onOpenChange={setShowReturnModal}
        onSuccess={() => {
          refetch();
        }}
      />

      {/* Modal de detalles */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Detalles de Devolución</DialogTitle>
          </DialogHeader>

          {selectedReturn && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Número</p>
                  <p className="font-mono font-bold">
                    {selectedReturn.returnNumber}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha</p>
                  <p>
                    {format(
                      new Date(selectedReturn.returnDate),
                      "dd/MM/yyyy HH:mm",
                      { locale: es },
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Venta Original
                  </p>
                  <p className="font-mono text-sm">
                    {selectedReturn.originalSaleId}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <p>{getStatusBadge(selectedReturn.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Método de Reembolso
                  </p>
                  <p>{getRefundMethodText(selectedReturn.refundMethod)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-lg font-bold text-primary">
                    {formatCurrency(
                      parseFloat(selectedReturn.totalAmount),
                      "CUP",
                    )}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Items Devueltos</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-center">Cant.</TableHead>
                        <TableHead className="text-right">Precio</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                        <TableHead>Condición</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReturn.items.map((item: any) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">
                            {item.product?.name || "Producto"}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(parseFloat(item.unitPrice), "CUP")}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(parseFloat(item.subtotal), "CUP")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {item.condition === "good" && "Bueno"}
                              {item.condition === "damaged" && "Dañado"}
                              {item.condition === "expired" && "Vencido"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {selectedReturn.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p className="bg-muted/30 p-3 rounded">
                    {selectedReturn.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
